import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Building2, Users, Settings } from 'lucide-react';
import { api } from '@/services/api';
import { PageHeader } from '@/components/layout/PageHeader';
import { useToast } from '@/hooks/use-toast';
import { ConfirmDialog } from '@/components/shared/ConfirmDialog';
import { StatCard } from '@/components/shared/StatCard';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { CreateWorkspaceDialog } from './CreateWorkspaceDialog';
import { WorkspaceCard } from './WorkspaceCard';
import { WorkspaceMembersDialog } from './WorkspaceMembersDialog';

interface Workspace {
  id: string;
  name: string;
  description: string | null;
  logo_url: string | null;
  role: string;
  members_count: number;
  created_at: string;
  deletion_scheduled_at: string | null;
}

export function WorkspacesPage() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [membersDialogOpen, setMembersDialogOpen] = useState(false);
  const [selectedWorkspace, setSelectedWorkspace] = useState<{ id: string; name: string } | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [workspaceToDelete, setWorkspaceToDelete] = useState<Workspace | null>(null);

  const { data: workspaces, isLoading } = useQuery<Workspace[]>({
    queryKey: ['workspaces'],
    queryFn: () => api.get('/workspaces').then((r) => r.data.data),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/workspaces/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workspaces'] });
      toast({ title: 'Workspace deleted', description: 'The workspace has been successfully deleted.' });
      setDeleteDialogOpen(false);
      setWorkspaceToDelete(null);
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error?.response?.data?.error?.message || 'Failed to delete workspace.',
        variant: 'destructive',
      });
    },
  });

  const totalWorkspaces = workspaces?.length ?? 0;
  const activeWorkspaces = workspaces?.filter((w) => !w.deletion_scheduled_at).length ?? 0;
  const totalMembers = workspaces?.reduce((sum, w) => sum + (w.members_count ?? 0), 0) ?? 0;

  const handleManageMembers = (workspace: Workspace) => {
    setSelectedWorkspace({ id: workspace.id, name: workspace.name });
    setMembersDialogOpen(true);
  };

  const handleDelete = (workspace: Workspace) => {
    setWorkspaceToDelete(workspace);
    setDeleteDialogOpen(true);
  };

  return (
    <div className="space-y-6">
      <PageHeader title="Workspaces" description="Manage your organization, team, or personal workspaces" actions={<CreateWorkspaceDialog />} />

      {/* Stat cards (matching old system StatCard style) */}
      <div className="grid gap-4 md:grid-cols-3">
        <StatCard label="Total Workspaces" value={totalWorkspaces} icon={Building2} color="purple" />
        <StatCard label="Active" value={activeWorkspaces} icon={Settings} color="green" animationDelay="0.05s" />
        <StatCard label="Total Members" value={totalMembers} icon={Users} color="blue" animationDelay="0.1s" />
      </div>

      {/* Workspace Cards */}
      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <Card key={i} className="p-4">
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <Skeleton className="h-10 w-10 rounded-full" />
                  <div className="space-y-1.5">
                    <Skeleton className="h-4 w-32" />
                    <Skeleton className="h-3 w-16" />
                  </div>
                </div>
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-2/3" />
                <div className="flex justify-between">
                  <Skeleton className="h-3 w-24" />
                  <Skeleton className="h-6 w-6 rounded" />
                </div>
              </div>
            </Card>
          ))}
        </div>
      ) : !workspaces || workspaces.length === 0 ? (
        <div className="flex flex-col items-center justify-center min-h-[400px] text-center p-8">
          <div className="rounded-full bg-muted p-6 mb-4">
            <Building2 className="h-12 w-12 text-muted-foreground" />
          </div>
          <h3 className="text-xl font-semibold mb-2">No workspaces yet</h3>
          <p className="text-muted-foreground mb-6 max-w-md">Create your first workspace to start organizing your teams and groups.</p>
          <CreateWorkspaceDialog />
        </div>
      ) : (
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {workspaces.map((workspace) => (
            <WorkspaceCard
              key={workspace.id}
              workspace={workspace}
              isOwner={workspace.role === 'owner'}
              isAdmin={workspace.role === 'admin'}
              onEdit={() => handleManageMembers(workspace)}
              onDelete={() => handleDelete(workspace)}
            />
          ))}
        </div>
      )}

      {/* Members Dialog */}
      <WorkspaceMembersDialog
        open={membersDialogOpen}
        onOpenChange={setMembersDialogOpen}
        workspace={selectedWorkspace}
      />

      {/* Delete Confirmation */}
      <ConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title="Delete Workspace"
        description={`Are you sure you want to delete "${workspaceToDelete?.name}"? This action cannot be undone and all associated data will be permanently removed.`}
        confirmLabel="Delete Workspace"
        variant="destructive"
        onConfirm={() => {
          if (workspaceToDelete) {
            deleteMutation.mutate(workspaceToDelete.id);
          }
        }}
        isLoading={deleteMutation.isPending}
      />
    </div>
  );
}
